import { useEffect, useRef, useState } from 'react';
import { useGridFilter } from 'ag-grid-react';
import { Form } from 'antd';
import { observer } from 'mobx-react-lite';

import { useResource } from 'app/common/api';
import { useNotifyFactory } from 'app/common/notifications/features/notifications';
import { useAuthStatusWriter } from 'app/railways/domains/auth/features/auth-status';
import { Loading } from 'app/ui';

import { AnyOfFilterForm } from '../any-of/any-of-filter.form';
import { createFilterComponent } from '../create-data-grid-filter';
import {
  AnyOfAsyncFilterModel,
  AnyOfAsyncFilterParams,
  createAnyOfAsyncFilterModel,
  RenderOptionsList,
} from './core';

export const AnyOfAsyncFilterComponent = createFilterComponent<
  AnyOfAsyncFilterModel,
  AnyOfAsyncFilterParams
>(
  observer(({ onModelChange, model, filterParams }) => {
    const { filterName, closeWhenChanged, asyncData } = filterParams;
    const shouldClosePopupAfterChange = closeWhenChanged ?? true;
    const hidePopupRef = useRef<(() => void) | undefined>(() => {});
    const hidePopup = () => hidePopupRef.current?.();

    const authStatusStore = useAuthStatusWriter();
    const notify = useNotifyFactory();

    const [options, setOptions] = useState<RenderOptionsList>([]);

    const optionsResource = useResource<void, unknown>({
      request: asyncData.request,
      errorNotify: notify,
      onAuthRequired: () => authStatusStore.denyAccess(),
      onDone(result) {
        const options = asyncData.prepareData(result);
        setOptions(options);
      },
    });

    const [form] = Form.useForm();

    useEffect(() => {
      optionsResource.send();
    }, [optionsResource]);

    useGridFilter({
      // use for disable client-side filtering
      doesFilterPass: () => true,
      afterGuiAttached: (params) => {
        hidePopupRef.current = params?.hidePopup;
      },
    });

    const onSubmit = (value: AnyOfAsyncFilterModel['value']) => {
      const model = createAnyOfAsyncFilterModel({
        value,
        filterName,
      });

      onModelChange(model);

      if (shouldClosePopupAfterChange) hidePopup();
    };

    const onReset = () => {
      form.resetFields();
      onModelChange(null);

      if (shouldClosePopupAfterChange) hidePopup();
    };

    return (
      <Loading active={optionsResource.isLoading}>
        <AnyOfFilterForm
          options={options}
          form={form}
          value={model?.value}
          onSubmit={onSubmit}
          onReset={onReset}
        />
      </Loading>
    );
  }),
);
